// Project costing — what a job actually took, in the shape a future quote gets priced from.
// Pure. Hours come from two independent sources and are kept apart on purpose:
//   clocked  = work clock sessions on the project (what you'd be paid for)
//   focused  = time banked on tasks (what the work itself took)
// The gap between them is unfocused/overhead time on site.

import { jobTypeOf, earnedRevenue, jobCostsOf, labourCost as labourByPerson, profitOf, overheadFor, personRates, jobPaysFor, pieceCostByWorker, salaryCost, jobEquipmentCost } from './profit.js';
import { planStartFor, scopeTotals, extraRateFor } from './scope.js';
import { localDateStr } from './time.js';

const H = 3600000;
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const round2 = (v) => Math.round(v * 100) / 100;

export function computeProjectCosting({
  project = {}, tasks = [], sessions = [], expenses = [], sections = [], costRate = 0, now = Date.now(),
  pay = null, billRoles = [], job = null, dept = null, me = null, overhead = null, jobPays = [], policy = null, weekStart = 0, equipmentRates = null,
} = {}) {
  const pid = project.id;
  const mine = tasks.filter((t) => t.projectId === pid);
  const sess = sessions.filter((s) => s.projectId === pid && !s.overlay && s.type !== 'travel');
  // Travel overlays carry no projectId; attribute a segment when its task belongs to this project.
  const taskIds = new Set(mine.map((t) => t.id));
  // A session or drive still open from an earlier day was orphaned, not worked: it never runs to now.
  const openToday = (at) => localDateStr(at) === localDateStr(now);
  let travelMs = 0; let travelM = 0;
  sessions.filter((s) => s.type === 'travel').forEach((s) => {
    const dur = num(s.duration) || (s.endedAt ? s.endedAt - s.startedAt : 0);
    const segs = Array.isArray(s.taskSegments) ? s.taskSegments : [];
    if (s.projectId === pid) { travelMs += dur; travelM += num(s.distanceM); return; }
    const segMs = segs.filter((g) => g.projectId === pid || taskIds.has(g.taskId))
      .reduce((m, g) => m + Math.max(0, (g.endedAt || s.endedAt || (openToday(g.startedAt) ? now : g.startedAt)) - g.startedAt), 0);
    if (segMs > 0) { travelMs += segMs; travelM += dur > 0 ? num(s.distanceM) * (segMs / dur) : 0; }
  });

  const clockedMs = sess.reduce((m, s) => m + (s.endedAt ? (num(s.duration) || s.endedAt - s.startedAt) : openToday(s.startedAt) ? Math.max(0, now - s.startedAt) : num(s.focusedTime)), 0);
  const focusedMs = mine.reduce((m, t) => m + num(t.timeSpent), 0);
  const breakMs = mine.reduce((m, t) => m + num(t.pauseTime), 0);
  // A job tracked only by task timers (never clocked in) still has labour.
  const labourMs = Math.max(clockedMs, focusedMs);

  const rate = num(costRate);
  const billRate = num(project.hourlyRate);
  const labourCost = round2((labourMs / H) * rate);
  const labourValue = round2((labourMs / H) * billRate);

  const spend = expenses.filter((e) => e.projectId === pid && e.type !== 'income');
  const income = expenses.filter((e) => e.projectId === pid && e.type === 'income');
  const expenseTotal = round2(spend.reduce((m, e) => m + num(e.amount), 0));
  const incomeTotal = round2(income.reduce((m, e) => m + num(e.amount), 0));
  const byCategory = Object.entries(spend.reduce((acc, e) => { const k = e.category || 'other'; acc[k] = (acc[k] || 0) + num(e.amount); return acc; }, {}))
    .map(([category, amount]) => ({ category, amount: round2(amount) })).sort((a, b) => b.amount - a.amount);

  const earned = round2(mine.reduce((m, t) => m + num(t.actualValue || t.moneyEarned), 0));
  const revenue = round2(incomeTotal + earned);
  const totalCost = round2(labourCost + expenseTotal);
  const budget = num(project.budget);
  // Value = what the job pays (the contract), apart from the budget (what we plan to spend).
  // Received = income logged on the project; margin here is against the value, not what's in so far.
  const value = num(project.value);

  // An estimate counts once: a parent whose subtasks carry their own estimates (an imported bid
  // assembly = the sum of its labour lines) is not added on top of them.
  const estimatedParents = new Set(mine.filter((t) => t.parentId && num(t.estimatedMinutes) > 0).map((t) => t.parentId));
  const ownEst = (t) => (estimatedParents.has(t.id) ? 0 : num(t.estimatedMinutes) * 60000);
  const group = (keyFn, labelFn) => {
    const m = new Map();
    mine.forEach((t) => {
      const k = keyFn(t);
      const g = m.get(k) || { key: k, label: labelFn(k), ms: 0, estMs: 0, tasks: 0, done: 0, jumps: 0, ranked: 0 };
      g.ms += num(t.timeSpent); g.estMs += ownEst(t); g.tasks += 1; if (t.status === 'done') g.done += 1;
      if (t.completion?.plannedRank) { g.jumps += t.completion.plannedRank - 1; g.ranked += 1; }
      m.set(k, g);
    });
    return [...m.values()].filter((g) => g.ms > 0 || g.tasks > 0)
      .map(({ jumps, ranked, ...g }) => ({ ...g, drift: ranked ? Math.round((jumps / ranked) * 10) / 10 : null }))
      .sort((a, b) => b.ms - a.ms);
  };
  const secName = new Map(sections.map((s) => [s.id, s.title || s.name || 'Section']));
  const BUCKET_LABEL = { setup: 'Setup (daily)', cleanup: 'Cleanup (daily)' };
  const bySection = group((t) => (t.bucket ? `bucket:${t.bucket}` : t.sectionId || ''),
    (k) => (k.startsWith('bucket:') ? BUCKET_LABEL[k.slice(7)] || k.slice(7) : k ? secName.get(k) || 'Section' : 'No section'));
  const byType = group((t) => t.taskType || 'task', (k) => k.charAt(0).toUpperCase() + k.slice(1));

  // Estimate accuracy: only finished tasks that had an estimate AND tracked time.
  const judged = mine.filter((t) => t.status === 'done' && ownEst(t) > 0 && num(t.timeSpent) > 0);
  const estMs = judged.reduce((m, t) => m + ownEst(t), 0);
  const actMs = judged.reduce((m, t) => m + num(t.timeSpent), 0);

  const topTasks = mine.filter((t) => num(t.timeSpent) > 0).sort((a, b) => num(b.timeSpent) - num(a.timeSpent)).slice(0, 8)
    .map((t) => ({ id: t.id, title: t.title, ms: num(t.timeSpent), estMs: num(t.estimatedMinutes) * 60000, done: t.status === 'done' }));

  // The CFO view (phase 1): each person's hours at their own rate, both versions, against what the
  // work earned. `pay` = { [uid]: opsPay doc }; a session with no ownerUid is the owner's (me), as is
  // task-timer time beyond what was clocked.
  let cfo = null;
  if (pay) {
    const msByUid = new Map();
    const add = (uid, ms) => msByUid.set(uid, (msByUid.get(uid) || 0) + ms);
    const meId = me || project.ownerUid || '_me';
    sess.forEach((s) => add(s.ownerUid || meId, s.endedAt ? (num(s.duration) || s.endedAt - s.startedAt) : openToday(s.startedAt) ? Math.max(0, now - s.startedAt) : num(s.focusedTime)));
    if (focusedMs > clockedMs) add(meId, focusedMs - clockedMs);
    // Phase 3: per-job pay (the wizard), piece pay on finished tasks, salary per pay week.
    const onJob = jobPaysFor(jobPays, pid);
    const pieces = pieceCostByWorker(tasks, pid);
    const overrides = {};
    for (const uid of msByUid.keys()) {
      const r = personRates(pay[uid] || null, onJob[uid] || null, policy);
      if (r.payType === 'piece') overrides[uid] = { actual: pieces[uid]?.amount || 0, paidOut: pieces[uid]?.amount || 0, basis: `${pieces[uid]?.count || 0} pieces` };
      else if (r.payType === 'salary' && pay[uid]?.salary && !onJob[uid]) {
        const sc = salaryCost({ uid, salary: pay[uid].salary, sessions, weekStart, burdenPct: num(pay[uid]?.burden?.pct), now });
        const cost = sess.filter((x) => (x.ownerUid || meId) === uid).reduce((a, x) => a + sc.costOf(x), 0);
        overrides[uid] = { actual: cost, paidOut: Math.max(cost, (msByUid.get(uid) / H) * r.paidOut), basis: 'salary ÷ hours each week' };
      }
    }
    for (const [uid, p] of Object.entries(pieces)) if (!msByUid.has(uid)) { msByUid.set(uid, 0); overrides[uid] = { actual: p.amount, paidOut: p.amount, basis: `${p.count} pieces` }; }
    const labour = labourByPerson(msByUid, pay, { jobPays: onJob, overrides, policy });
    const hours = labourMs / H;
    const type = jobTypeOf(project, job, dept);
    const revenue = earnedRevenue({ type, project, job, dept, tasks: mine.filter((t) => !t.bucket), hours, booked: incomeTotal });
    const jc = jobCostsOf(job);
    // Phase 3: the truck per km on this job, assigned tools per hour.
    const equipment = equipmentRates ? jobEquipmentCost({ project, km: travelM / 1000, hours, rates: equipmentRates }) : { total: 0, lines: [] };
    const jobCosts = round2((jc == null ? expenseTotal : jc) + equipment.total);
    const oh = overheadFor(type, hours, overhead);   // phase 2: overhead per billable hour × this job's hours
    cfo = { type, linked: !!job, hours: round2(hours), revenue, jobCosts, jobCostsFrom: jc == null ? 'ops' : 'books', equipment, labour, overhead: oh, overheadRate: overhead || null, ...profitOf({ revenue: revenue.amount, jobCosts, labour, hours, overhead: oh }) };
  }
  // Scope: what the quote covered (included), billed on top (extra), ours (missed), never priced (overhead).
  const planAt = planStartFor(project, sessions, mine);
  const sc = scopeTotals(mine, { planAt });
  const extraOwn = extraRateFor(project, pay?.[me] || null, billRoles);
  const scopeCost = (ms) => round2((ms / H) * rate);
  const byScope = {
    quoted: sc.quoted, planAt,
    ...Object.fromEntries(['included', 'extra', 'missed', 'overhead', 'unsorted', 'none'].map((k) => [k, { ...sc[k], cost: scopeCost(sc[k].ms) }])),
    earningMs: sc.earningMs, billedOnTopMs: sc.billedOnTopMs, leakMs: sc.leakMs,
    extraRate: extraOwn.rate, extraRateFrom: extraOwn.from,
    extraValue: extraOwn.rate ? round2((sc.extra.ms / H) * extraOwn.rate) : null,
  };

  const roots = mine.filter((t) => !t.parentId);
  return {
    cfo,
    hours: { clockedMs, focusedMs, breakMs, travelMs, labourMs, overheadMs: Math.max(0, clockedMs - focusedMs), travelKm: round2(travelM / 1000), days: new Set(sess.map((s) => s.date).filter(Boolean)).size },
    money: {
      costRate: rate, billRate, labourCost, labourValue, expenseTotal, incomeTotal, earned, revenue, totalCost,
      margin: round2(revenue - totalCost), marginPct: revenue > 0 ? Math.round(((revenue - totalCost) / revenue) * 100) : null,
      costPerHour: labourMs > 0 ? round2(totalCost / (labourMs / H)) : null,
    },
    budget: { budget, used: totalCost, remaining: round2(budget - totalCost), pct: budget > 0 ? Math.round((totalCost / budget) * 100) : null },
    value: value > 0
      ? { value, received: incomeTotal, outstanding: round2(value - incomeTotal), receivedPct: Math.round((incomeTotal / value) * 100),
        margin: round2(value - totalCost), marginPct: Math.round(((value - totalCost) / value) * 100) }
      : { value: 0, received: incomeTotal, outstanding: null, receivedPct: null, margin: null, marginPct: null },
    progress: { total: roots.length, done: roots.filter((t) => t.status === 'done').length },
    estimate: { judged: judged.length, estMs, actMs, ratio: estMs > 0 ? round2(actMs / estMs) : null },
    byCategory, bySection, byType, topTasks, byScope,
  };
}
